import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Project, Category } from '../types';
import ScrollToTop from '../components/ScrollToTop';

const Projects: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<(number | null)[]>([null]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // 设置浏览器标签标题
  useEffect(() => {
    document.title = '东大国际';
  }, []);

  // 处理URL查询参数
  useEffect(() => {
    // 从URL获取category参数
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    
    // 如果URL中有category参数且已加载分类数据
    if (categoryParam && categories.length > 0) {
      const categoryId = parseInt(categoryParam, 10);
      
      // 确认这个分类ID在我们的分类列表中
      if (!isNaN(categoryId) && categories.some(cat => cat.id === categoryId)) {
        setSelectedCategories([categoryId]);
      }
    }
  }, [location.search, categories]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, categoriesRes] = await Promise.all([
          api.get('/projects'),
          api.get('/categories')
        ]);
        setProjects(projectsRes.data);
        const sortedCategories = categoriesRes.data.slice().sort((a: Category, b: Category) => {
          if (a.display_order === undefined && b.display_order === undefined) return 0;
          if (a.display_order === undefined) return 1;
          if (b.display_order === undefined) return -1;
          return a.display_order - b.display_order;
        });
        setCategories(sortedCategories);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCategorySelect = (categoryId: number | null) => {
    setSelectedCategories([categoryId]);
  };

  const filteredProjects = projects.filter(project => {
    // Filter out projects without image_url or with empty image_url
    if (!project.image_url || project.image_url.trim() === '') {
      return false;
    }
    
    if (selectedCategories.includes(null)) {
      return true;
    }
    return project.category_ids.some(id => selectedCategories.includes(id));
  });

  // 获取当前标题
  const getTitle = () => {
    if (selectedCategories.includes(null)) {
      return i18n.language === 'zh' ? '全部项目' : 'All Projects';
    }
    if (selectedCategories.length === 1) {
      const category = categories.find(c => c.id === selectedCategories[0]);
      return category ? (i18n.language === 'zh' ? category.name : category.name_en) : (i18n.language === 'zh' ? '筛选结果' : 'Filter Results');
    }
    return i18n.language === 'zh' ? '筛选结果' : 'Filter Results';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900 mb-4"></div>
              <p className="text-gray-600 font-medium">{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[80px] min-h-screen bg-gray-50 font-PingFang">
      <ScrollToTop />
      <section className="py-10 bg-white">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* 筛选栏 */}
          <div className="mb-12">
            <div className="flex flex-wrap gap-8">
              {/* 全部选项 */}
              <button
                onClick={() => handleCategorySelect(null)}
                className={`py-2.5 text-base transition-colors ${
                  selectedCategories.includes(null)
                    ? 'text-gray-900 font-semibold'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('projects.all')}
              </button>
              
              {/* 分类选项 */}
              {categories.map((category: Category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className={`py-2.5 text-base transition-colors ${
                    selectedCategories.includes(category.id)
                      ? 'text-gray-900 font-semibold'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {i18n.language === 'zh' ? category.name : category.name_en}
                </button>
              ))}
            </div>
          </div>

          {/* 标题和视图切换 */}
          <div className="relative mb-24">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-bold">{getTitle()}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="absolute -bottom-6 left-0 right-0 h-[1px] bg-gray-300" />
          </div>

          {/* 项目展示 */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
              {filteredProjects.map((project: Project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group block bg-white"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={i18n.language === 'zh' ? project.title : project.title_en}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {i18n.language === 'zh' ? project.title : project.title_en}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {i18n.language === 'zh' ? project.description : project.description_en}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProjects.map((project: Project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group block py-5 border-b border-gray-100"
                >
                  <div className="flex justify-between items-center">
                    <div className="w-48">
                      <h3 className="text-base font-medium text-gray-900">
                        {i18n.language === 'zh' ? project.title : project.title_en}
                      </h3>
                    </div>
                    <div className="flex-1 px-12">
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {i18n.language === 'zh' ? project.description : project.description_en}
                      </p>
                    </div>
                    <div className="w-24 text-right">
                      <span className="text-sm text-gray-500 border-b border-gray-300 pb-0.5">
                        {i18n.language === 'zh' ? '查看详情' : 'View Details'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* 无结果提示 */}
          {filteredProjects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-gray-500 text-lg mb-4">
                {t('projects.noResults')}
              </p>
              <button 
                onClick={() => setSelectedCategories([null])}
                className="px-6 py-2.5 text-base text-gray-900 hover:text-gray-600 transition-colors"
              >
                {t('projects.resetFilters')}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects; 
